"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import type { HoldingShapeVariant } from "@repo/ui/holding-shape";
import { cn } from "@repo/ui/utils";
import type { VisualType } from "@/lib/types";
import { GridCellComponent } from "./grid-cell.component";
import type { GridBgColor, GridCell, ImageShapeGridProps, SolidShapeCell } from "./image-shape-grid.types";
const CYCLE_INTERVAL_MS = 1500;
const VISIBLE_CELL_COUNT = 6;

/** Solid shape variants the grid can show. Cycle pool, not tied to a position. */
const SOLID_POOL: SolidShapeCell[] = [{
  type: "solid",
  shape: "rounded-rectangle-stack",
  bgColor: "avocado",
  shapeColor: "light-green"
}, {
  type: "solid",
  shape: "starburst",
  bgColor: "mint",
  shapeColor: "light-green"
}, {
  type: "solid",
  shape: "diamond",
  bgColor: "peach",
  shapeColor: "orange"
}, {
  type: "solid",
  shape: "circle",
  bgColor: "turquoise",
  shapeColor: "sea"
}, {
  type: "solid",
  shape: "squircle",
  bgColor: "sky-blue",
  shapeColor: "cobalt"
}, {
  type: "solid",
  shape: "arch",
  bgColor: "yellow",
  shapeColor: "orange"
}, {
  type: "solid",
  shape: "concave-square",
  bgColor: "bright-blue",
  shapeColor: "cobalt"
}, {
  type: "solid",
  shape: "pinwheel",
  bgColor: "mint",
  shapeColor: "turquoise"
}, {
  type: "solid",
  shape: "sunburst",
  bgColor: "avocado",
  shapeColor: "light-green"
}];
type ShapedVariant = {
  shape: HoldingShapeVariant;
  bgColor: GridBgColor;
};

/** Holding-shape variants applied when a visual is shown as a shaped image. */
const SHAPED_VARIANTS: ShapedVariant[] = [{
  shape: "arch",
  bgColor: "bright-blue"
}, {
  shape: "concave-square",
  bgColor: "peach"
}, {
  shape: "circle",
  bgColor: "turquoise"
}, {
  shape: "squircle",
  bgColor: "sky-blue"
}, {
  shape: "diamond",
  bgColor: "yellow"
}, {
  shape: "starburst",
  bgColor: "mint"
}];

/**
 * Initial layout for the first paint — matches the original Figma:
 * image, solid, shaped, solid, shaped, image. After hydration, positions
 * cycle freely and may end up showing any block type.
 */
type InitialSlot = "image" | "shaped" | "solid";
const INITIAL_LAYOUT: InitialSlot[] = ["image", "solid", "shaped", "solid", "shaped", "image"];

/**
 * Static fallback — used when `cells` is passed directly or `animated={false}`.
 * Expands the initial layout to fit all visuals.
 */
function buildGridCells(visuals: VisualType[]): GridCell[] {
  const cells: GridCell[] = [];
  let visualIndex = 0;
  let patternIndex = 0;
  let shapedOrder = 0;
  let solidOrder = 0;
  while (visualIndex < visuals.length) {
    const slot = INITIAL_LAYOUT[patternIndex % INITIAL_LAYOUT.length]!;
    if (slot === "solid") {
      cells.push(SOLID_POOL[solidOrder++ % SOLID_POOL.length]!);
      patternIndex++;
      continue;
    }
    const visual = visuals[visualIndex];
    if (!visual) break;
    if (slot === "image") {
      cells.push({
        type: "image",
        visual
      });
    } else {
      const variant = SHAPED_VARIANTS[shapedOrder++ % SHAPED_VARIANTS.length]!;
      cells.push({
        type: "shaped",
        visual,
        shape: variant.shape,
        bgColor: variant.bgColor
      });
    }
    visualIndex++;
    patternIndex++;
  }
  return cells;
}

/**
 * One entry in the combined cycling pool. Each visual contributes two
 * entries (plain and shaped) so the same image can appear either way.
 * `identity` is used to enforce uniqueness across the 6 visible cells —
 * a visual shown as "image" is considered the same block as the same
 * visual shown as "shaped".
 */
type PoolEntry = {
  kind: "image";
  identity: string;
  animateKey: string;
  visualIndex: number;
} | {
  kind: "shaped";
  identity: string;
  animateKey: string;
  visualIndex: number;
  shape: HoldingShapeVariant;
  bgColor: GridBgColor;
} | {
  kind: "solid";
  identity: string;
  animateKey: string;
  solidIndex: number;
};
function buildPool(visuals: VisualType[]): PoolEntry[] {
  const pool: PoolEntry[] = [];
  visuals.forEach((visual, visualIndex) => {
    const visualKey = (visual.mediaSource as {
      _key?: string;
    } | undefined)?._key ?? `idx-${visualIndex}`;
    pool.push({
      kind: "image",
      identity: `v-${visualKey}`,
      animateKey: `image-${visualKey}`,
      visualIndex
    });
    const variant = SHAPED_VARIANTS[visualIndex % SHAPED_VARIANTS.length]!;
    pool.push({
      kind: "shaped",
      identity: `v-${visualKey}`,
      animateKey: `shaped-${visualKey}-${variant.shape}`,
      visualIndex,
      shape: variant.shape,
      bgColor: variant.bgColor
    });
  });
  SOLID_POOL.forEach((_, solidIndex) => {
    pool.push({
      kind: "solid",
      identity: `s-${solidIndex}`,
      animateKey: `solid-${solidIndex}`,
      solidIndex
    });
  });
  return pool;
}
function entryToCell(entry: PoolEntry, visuals: VisualType[]): GridCell {
  if (entry.kind === "solid") return SOLID_POOL[entry.solidIndex]!;
  const visual = visuals[entry.visualIndex]!;
  if (entry.kind === "image") return {
    type: "image",
    visual
  };
  return {
    type: "shaped",
    visual,
    shape: entry.shape,
    bgColor: entry.bgColor
  };
}

/**
 * Build the initial assignment to match INITIAL_LAYOUT where possible —
 * gives a stable, design-faithful first paint that then organically
 * shuffles as cycling kicks in.
 */
function pickInitial(pool: PoolEntry[]): number[] {
  const used = new Set<string>();
  const result: number[] = [];
  for (let pos = 0; pos < VISIBLE_CELL_COUNT; pos++) {
    const want = INITIAL_LAYOUT[pos]!;
    const wantedKind: PoolEntry["kind"] = want;
    let chosen = pool.findIndex(e => e.kind === wantedKind && !used.has(e.identity));
    if (chosen < 0) {
      // fall back to any unused entry
      chosen = pool.findIndex(e => !used.has(e.identity));
    }
    if (chosen < 0) chosen = 0;
    used.add(pool[chosen]!.identity);
    result.push(chosen);
  }
  return result;
}

/**
 * Order positions are refreshed in. Looks random to a viewer but guarantees
 * every position swaps once per round before any swaps a second time.
 *
 * Grid (3 cols × 2 rows):
 *   0 = top-left,   1 = top-middle,    2 = top-right
 *   3 = bottom-left, 4 = bottom-middle, 5 = bottom-right
 */
const SWAP_SEQUENCE: readonly number[] = [0, 4, 2, 3, 1, 5];

/**
 * Position type per grid position. Each position is locked to its own
 * pool — plain image, shaped image, and solid color blocks never swap
 * across categories.
 */
type PositionType = PoolEntry["kind"];
const POSITION_TYPES: PositionType[] = INITIAL_LAYOUT;
function entryMatchesPositionType(entry: PoolEntry, positionType: PositionType): boolean {
  return entry.kind === positionType;
}

/**
 * Returns the next pool entry index that isn't currently on screen
 * (by visual identity) AND matches the position's type, scanning the
 * pool sequentially from `cursor`. Returns -1 when none exists.
 */
function nextOffScreenEntry(pool: PoolEntry[], assignment: number[], position: number, cursor: number): number {
  const positionType = POSITION_TYPES[position]!;
  const onScreen = new Set<string>();
  assignment.forEach((idx, i) => {
    if (i !== position) onScreen.add(pool[idx]!.identity);
  });
  const current = assignment[position]!;
  for (let offset = 0; offset < pool.length; offset++) {
    const candidate = (cursor + offset) % pool.length;
    if (candidate === current) continue;
    if (!entryMatchesPositionType(pool[candidate]!, positionType)) continue;
    if (onScreen.has(pool[candidate]!.identity)) continue;
    return candidate;
  }
  return -1;
}
export function ImageShapeGrid(t0) {
  const $ = _c(11);
  const {
    visuals,
    cells,
    animated: t1,
    className
  } = t0;
  const animated = t1 === undefined ? true : t1;
  if (cells && cells.length > 0) {
    let t2;
    if ($[0] !== cells || $[1] !== className) {
      t2 = <StaticGrid cells={cells} className={className} />;
      $[0] = cells;
      $[1] = className;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    return t2;
  }
  if (visuals && visuals.length > 0) {
    if (animated) {
      let t2;
      if ($[3] !== className || $[4] !== visuals) {
        t2 = <AnimatedGrid visuals={visuals} className={className} />;
        $[3] = className;
        $[4] = visuals;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      return t2;
    }
    let t2;
    if ($[6] !== visuals) {
      t2 = buildGridCells(visuals);
      $[6] = visuals;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== className || $[9] !== t2) {
      t3 = <StaticGrid cells={t2} className={className} />;
      $[8] = className;
      $[9] = t2;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  return null;
}
function StaticGrid(t0) {
  const $ = _c(7);
  const {
    cells,
    className
  } = t0;
  if (cells.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("grid w-full grid-cols-2 tablet:grid-cols-3", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== cells) {
    t2 = cells.map(_temp);
    $[2] = cells;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className={t1} data-testid="image-shape-grid">{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function _temp(cell, index) {
  return <GridCellComponent key={`${cell.type}-${index}`} cell={cell} />;
}
function AnimatedGrid(t0) {
  const $ = _c(27);
  const {
    visuals,
    className
  } = t0;
  const prefersReducedMotion = useReducedMotion();
  let t1;
  if ($[0] !== visuals) {
    t1 = buildPool(visuals);
    $[0] = visuals;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pool = t1;
  const [storedPool, setStoredPool] = useState(pool);
  let t2;
  if ($[2] !== pool) {
    t2 = () => pickInitial(pool);
    $[2] = pool;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [assignment, setAssignment] = useState(t2);
  if (storedPool !== pool) {
    setStoredPool(pool);
    setAssignment(pickInitial(pool));
  }
  const assignmentRef = useRef(assignment);
  let t3;
  let t4;
  if ($[4] !== assignment) {
    t3 = () => {
      assignmentRef.current = assignment;
    };
    t4 = [assignment];
    $[4] = assignment;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  const stepRef = useRef(0);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      image: 0,
      shaped: 0,
      solid: 0
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const cursorsRef = useRef(t5);
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      stepRef.current = 0;
      cursorsRef.current = {
        image: 0,
        shaped: 0,
        solid: 0
      };
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== pool) {
    t7 = [pool];
    $[9] = pool;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[11] !== pool || $[12] !== prefersReducedMotion) {
    t8 = () => {
      if (prefersReducedMotion) {
        return;
      }
      const cyclable = (["image", "shaped", "solid"] as PositionType[]).some(type => pool.filter(e => e.kind === type).length > POSITION_TYPES.filter(t => t === type).length);
      if (!cyclable) {
        return;
      }
      const interval = window.setInterval(() => {
        const current = assignmentRef.current;
        const position = SWAP_SEQUENCE[stepRef.current % SWAP_SEQUENCE.length];
        stepRef.current = stepRef.current + 1;
        const positionType = POSITION_TYPES[position];
        const nextIndex = nextOffScreenEntry(pool, current, position, cursorsRef.current[positionType]);
        if (nextIndex < 0) {
          return;
        }
        cursorsRef.current[positionType] = (nextIndex + 1) % pool.length;
        const next = current.slice();
        next[position] = nextIndex;
        setAssignment(next);
      }, CYCLE_INTERVAL_MS);
      return () => window.clearInterval(interval);
    };
    t9 = [pool, prefersReducedMotion];
    $[11] = pool;
    $[12] = prefersReducedMotion;
    $[13] = t8;
    $[14] = t9;
  } else {
    t8 = $[13];
    t9 = $[14];
  }
  useEffect(t8, t9);
  let t10;
  if ($[15] !== className) {
    t10 = cn("grid w-full grid-cols-2 tablet:grid-cols-3", className);
    $[15] = className;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== assignment || $[18] !== pool || $[19] !== visuals) {
    let t12;
    if ($[21] !== pool || $[22] !== visuals) {
      t12 = (entryIndex, position_0) => {
        const entry = pool[entryIndex];
        const cell = entryToCell(entry, visuals);
        return <GridCellComponent key={`pos-${position_0}`} cell={cell} animateKey={entry.animateKey} />;
      };
      $[21] = pool;
      $[22] = visuals;
      $[23] = t12;
    } else {
      t12 = $[23];
    }
    t11 = assignment.map(t12);
    $[17] = assignment;
    $[18] = pool;
    $[19] = visuals;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <div className={t10} data-testid="image-shape-grid">{t11}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}